/*
 * Copyright (c) 2017 Datarista, Inc.
 */

/* eslint-disable max-len */

// For local nginx / reverse proxy:
export const API_URI = '/accountservice/swagger.json';

// For stand-alone test server:
// export const API_URI = 'http://accountservice-test.datarista.net/swagger.json';

export const APP_SESSION_TIMEOUT = 1440 * 60 * 1000; // 1440 minutes
export const APP_SESSION_TIME_TO_CLICK = 5 * 60 * 1000; // 5 minutes
export const APP_SESSION_WARNING_TEXT =
  "We noticed you haven't interacted with this page for a while. For your safety, your session will be timed out in five minutes. If you'd like more time, just click 'Stay Logged In' below. If you're done and would like to log out, click 'Log Out'.";

export const APP_TOKEN_POLL = 1 * 60 * 1000; // 1 minute
export const APP_TOKEN_EXPIRATION = 10 * 60 * 1000; // 10 minutes
export const APP_TOKEN_REFRESH_TIME = 2 * 60 * 1000; // 2 minutes

export const APP_NOTIFICATION_TIMEOUT = 1 * 60 * 1000; // 1 minute

export const APP_PASSWORD_LIMITS = 'Passwords must be at least eight characters long.';

export const USERS_INVITE_NOTICE_EXPIRATION_TIME = 10 * 365 * 24 * 60 * 60 * 1000; // 10 years

// Date and Time Formats
export const APP_DATE_FORMAT = 'MMMM D, YYYY';
export const SIMPLE_DATE_FORMAT = 'MM/DD/YYYY';
export const SIMPLE_DATETIME_FORMAT = 'MM/DD/YYYY HH:MM:ss';

// Role levels - Top level
export const SUPER_ADMIN = 1;
export const PROVIDER_ADMIN = 2;
export const PROVIDER_SALES_OPS = 3;
export const PROVIDER_TECHNICAL = 4;
export const PROVIDER_BILLING = 5;
export const PORTAL_USER = 6;

// User Roles Page Permissions
export const SPECIAL_PATH_ACCESS_LEVELS = {
  editList: [SUPER_ADMIN, PROVIDER_ADMIN, PROVIDER_TECHNICAL],
  // 'data/dataset/datasetId/create-group'
  'data/dataset/\\d+/create-group': [
    SUPER_ADMIN,
    PROVIDER_ADMIN,
    PROVIDER_SALES_OPS,
    PROVIDER_TECHNICAL,
  ],
  // 'data/dataset/datasetId/create-sku'
  'data/dataset/\\d+/create-sku': [
    SUPER_ADMIN,
    PROVIDER_ADMIN,
    PROVIDER_SALES_OPS,
    PROVIDER_TECHNICAL,
  ],
  // 'data/dataset/datasetId/edit-sku/fieldsetId'
  'data/dataset/\\d+/edit-sku/\\d+': [
    SUPER_ADMIN,
    PROVIDER_ADMIN,
    PROVIDER_SALES_OPS,
    PROVIDER_TECHNICAL,
  ],
  // 'data/dataset/datasetId/edit-group/fieldsetId'
  'data/dataset/\\d+/edit-group/\\d+': [
    SUPER_ADMIN,
    PROVIDER_ADMIN,
    PROVIDER_SALES_OPS,
    PROVIDER_TECHNICAL,
  ],
  // 'data/newList/accountId'
  'data/newList/\\d+': [SUPER_ADMIN, PROVIDER_ADMIN, PROVIDER_TECHNICAL],
  // 'orders/create/page/selectedAccountId'
  'orders/create/\\d+/\\d+': [SUPER_ADMIN],
  // 'orders/create/page'
  'orders/create/\\d+': [SUPER_ADMIN, PROVIDER_ADMIN, PROVIDER_SALES_OPS],
  // 'orders/order/upload-prematched/orderId'
  'orders/order/upload-prematched/\\d+': [SUPER_ADMIN, PROVIDER_ADMIN, PROVIDER_SALES_OPS],
};

// Roll levels - Customer portal Installation level
export const INSTALL_ADMIN = 'ADMIN';
export const INSTALL_USER = 'USER';

// Fieldset types
export const FIELDSET_TYPE_GROUP = 1;
export const FIELDSET_TYPE_SKU = 2;

// Data Pages
export const NEW_GROUP_PAGE = 'create-group';
export const NEW_SKU_PAGE = 'create-sku';
export const EDIT_GROUP_PAGE = 'edit-group';
export const EDIT_SKU_PAGE = 'edit-sku';

// Record Level Types
export const RECORD_LEVEL_TYPE_COMPANY = 'B2B_COMPANY';
export const RECORD_LEVEL_TYPE_CONTACT = 'B2B_CONTACT';
export const RECORD_LEVEL_TYPE_LEAD = 'B2B_LEAD';
export const RECORD_LEVEL_TYPE_ITEM = 'B2B_ITEM';

export const RECORD_TYPE_ACCOUNT = 'ACCOUNT';
export const RECORD_TYPE_CONTACT = 'CONTACT';
export const RECORD_TYPE_LEAD = 'LEAD';
export const RECORD_TYPE_LEAD_ACCOUNT = 'LEAD_ACCOUNT';
export const RECORD_TYPE_ITEM = 'ITEM';

export const getRecordLevelTypeDisplayName = recordLevelTypeId => {
  switch (recordLevelTypeId) {
    case 1:
      return 'Account';
    case 2:
      return 'Contact';
    case 3:
      return 'Lead';
    case 4:
      return 'Item';
    default:
      return '';
  }
};

export const getRecordTypeCode = recordTypeId => {
  switch (recordTypeId) {
    case 1:
      return RECORD_LEVEL_TYPE_COMPANY;
    case 2:
      return RECORD_LEVEL_TYPE_CONTACT;
    case 3:
      return RECORD_LEVEL_TYPE_LEAD;
    case 4:
      return RECORD_LEVEL_TYPE_ITEM;
    default:
      return '';
  }
};

export const getRecordTypeId = recordType => {
  switch (recordType) {
    case RECORD_TYPE_ACCOUNT:
      return 0;
    case RECORD_TYPE_CONTACT:
      return 1;
    case RECORD_TYPE_LEAD:
      return 2;
    case RECORD_TYPE_ITEM:
      return 3;
    default:
      return -1;
  }
};

// Use Case Types (Used in the order form)
export const UCT_FULL_FILE = 'FULL_FILE';
export const UCT_SEGMENT_BUILDER = 'SEGMENT_BUILDER';

// Company Logo Upload
export const ACCEPTABLE_LOGO_FORMATS = ['.jpg', '.png', '.svg'];
export const MAX_LOGO_SIZE = 5000000; // 5Mb

// ORDER LIMITS
export const ORDER_MAX_RECORDS = 50000000; // 50,000K

// Password Regular Expressions
export const UPPER_LOWER_NUMBER_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/;
export const ONE_UPPERCASE_ONE_LOWERCASE_REGEX = /^(?=.*[a-z])(?=.*[A-Z]).+$/;
export const ONE_NUMBER_REGEX = /^(?=.*\d).+$/;

// Validation Regular Expressions
export const VALID_EMAIL_REGEX = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
// export const VALID_ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/i; // US Only, Add additional validations for different countries
export const VALID_DOLLAR_REGEX = /^\d*(\.\d{1,2})?$/;
// Adjusted to allow bare hostnames like http://localhost:9200/
export const VALID_URL_REGEX = /https?:\/\/(www\.)?[-a-z0-9@:%._+~#=]{2,256}\.?[a-z]{2,12}\b([-a-z0-9@:%_+.~#?&//=]*)/i;
// export const INVALID_WHITESPACE_REGEX = /^\s+|\s+$/;

// Time Related
export const SEARCH_DATE_FORMAT = 'YYYY/MM/DD HH:mm:ss.SSS';
export const SEARCH_DATE_DISPLAY_FORMAT = 'MM/DD/YYYY';
export const DEFAULT_TIME_POSTFIX = '00:00:00.000';
